import React, { useEffect, useState, useRef } from 'react';
import { Clock, AlertCircle, CheckCircle, User, Building, Landmark, SmartphoneIcon, BellRingIcon, ShieldCheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { formatPrice } from '../../utils/formatters';
interface RaastRTPPaymentProps {
  totalAmount: number;
  onComplete: () => void;
  onCancel: () => void;
  initialStep?: 'input' | 'confirm' | 'waiting';
}
type RtpStep = 'input' | 'confirm' | 'waiting';
export function RaastRTPPayment({
  totalAmount,
  onComplete,
  onCancel,
  initialStep
}: RaastRTPPaymentProps) {
  const [step, setStep] = useState<RtpStep>(initialStep ?? 'input');
  const [idType, setIdType] = useState<'iban' | 'alias'>('alias');
  const [idValue, setIdValue] = useState(initialStep && initialStep !== 'input' ? '03001234567' : '');
  const [error, setError] = useState('');
  // Timer state for waiting step
  const [timeLeft, setTimeLeft] = useState(300);
  const [isExpired, setIsExpired] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  // Mock account details
  const mockAccountTitle = 'Muhammad Shoaib';
  const mockBankName = 'Habib Bank Limited';
  const validateIBAN = (value: string): string | null => {
    const clean = value.replace(/\s/g, '').toUpperCase();
    if (clean.length !== 24) {
      return 'IBAN must be exactly 24 characters';
    }
    // Standard IBAN: 2 letter country code + 2 digits + 20 alphanumeric
    const ibanRegex = /^[A-Z]{2}\d{2}[A-Z0-9]{20}$/;
    if (!ibanRegex.test(clean)) {
      return 'Invalid IBAN format. Must start with 2-letter country code followed by 2 digits and 20 alphanumeric characters (e.g. PK36HABB0000001123456702)';
    }
    return null;
  };
  const validateAlias = (value: string): string | null => {
    const clean = value.replace(/\D/g, '');
    if (clean.length !== 11) {
      return 'Mobile number must be exactly 11 digits';
    }
    if (!clean.startsWith('03')) {
      return 'Mobile number must start with 03';
    }
    return null;
  };
  const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only alphanumeric and spaces for IBAN
    const val = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '').toUpperCase();
    setIdValue(val);
    setError('');
  };
  const handleAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only digits for mobile number
    const val = e.target.value.replace(/\D/g, '').slice(0, 11);
    setIdValue(val);
    setError('');
  };
  const handleValidate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (idType === 'iban') {
      const err = validateIBAN(idValue);
      if (err) {
        setError(err);
        return;
      }
    } else {
      const err = validateAlias(idValue);
      if (err) {
        setError(err);
        return;
      }
    }
    setStep('confirm');
  };
  const handleSendRequest = () => {
    setStep('waiting');
  };
  // Timer logic for waiting step
  useEffect(() => {
    if (step !== 'waiting') return;
    if (timeLeft <= 0) {
      setIsExpired(true);
      return;
    }
    const timerId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [step, timeLeft]);
  // Auto-redirect timer (10 seconds)
  useEffect(() => {
    if (step !== 'waiting' || isExpired) return;
    const successTimerId = setTimeout(() => {
      onCompleteRef.current();
    }, 50000);
    return () => clearTimeout(successTimerId);
  }, [step, isExpired]);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="flex flex-col py-2" data-id="element-662" data-mp="1l9i9hjidl5sp">
      <div className="text-center mb-6" data-id="element-663" data-mp="1lal2py367kbs">
        <img src="https://cdn.magicpatterns.com/uploads/oZFx4MBZmzvJA34NArst7p/RAAST_Logo.png" alt="RAAST" className="h-10 object-contain mx-auto mb-2" data-id="element-664" data-mp="1p8r2gm5lczn7" />
        <h2 className="text-xl font-bold text-gray-900" data-id="element-665" data-mp="1i8gidmu6za3t">
          Request to Pay (RTP)
        </h2>
      </div>

      {step === 'input' && <form onSubmit={handleValidate} className="space-y-6 max-w-sm mx-auto w-full" data-id="element-666" data-mp="18g11ksumsrki">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-6 flex justify-between items-center" data-id="element-667" data-mp="13i9ag11t99hd">
            <div data-id="element-668" data-mp="zge7ks48eo1h">
              <p className="text-sm text-blue-800 font-medium" data-id="element-669" data-mp="zgw6jwia3gd3">
                Payment Amount
              </p>
              <p className="text-xl font-bold text-blue-900" data-id="element-670" data-mp="ufq7lgy0ywv4">
                {formatPrice(totalAmount)}
              </p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-671" data-mp="kjn86iuk5cbv">
            <label className="block text-sm font-medium text-gray-700" data-id="element-672" data-mp="kkok0pjee2un">
              Select Identifier Type
            </label>
            <div className="grid grid-cols-2 gap-4" data-id="element-673" data-mp="2ewv7onthac3u">
              <label className={`border rounded-lg p-3 cursor-pointer flex items-center justify-center transition-colors ${idType === 'alias' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-674" data-mp="2ey694gzgb7rs">
                <input type="radio" name="idType" value="alias" checked={idType === 'alias'} onChange={() => {
              setIdType('alias');
              setIdValue('');
              setError('');
            }} className="sr-only" data-id="element-675" data-mp="2rde11h6ltu9c" />
                <span className="font-medium text-sm" data-id="element-676" data-mp="24fj5swc4y39c">Mobile Number</span>
              </label>
              <label className={`border rounded-lg p-3 cursor-pointer flex items-center justify-center transition-colors ${idType === 'iban' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-677" data-mp="28g33q24j9awj">
                <input type="radio" name="idType" value="iban" checked={idType === 'iban'} onChange={() => {
              setIdType('iban');
              setIdValue('');
              setError('');
            }} className="sr-only" data-id="element-678" data-mp="2iflmwgcmcsnj" />
                <span className="font-medium text-sm" data-id="element-679" data-mp="3tw234bvzzdbg">IBAN</span>
              </label>
            </div>
          </div>

          <div data-id="element-680" data-mp="2z6qu3ecdk5n3">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-681" data-mp="2z78qqc2y8lvj">
              {idType === 'alias' ? 'Enter Mobile Number (Alias)' : 'Enter IBAN'}
            </label>
            <input type="text" value={idValue} onChange={idType === 'alias' ? handleAliasChange : handleIBANChange} placeholder={idType === 'alias' ? '03XXXXXXXXX' : 'PK36HABB0000001123456702'} maxLength={idType === 'alias' ? 11 : 27} className={`w-full px-4 py-3 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono tracking-wide ${error ? 'border-red-500' : 'border-gray-300'}`} data-id="element-682" data-mp="394wvluu9r9rf" />
            {error && <p className="mt-1 text-sm text-red-600" data-id="element-683" data-mp="2qdvrkseako10">{error}</p>}
            <p className="mt-2 text-xs text-gray-500" data-id="element-684" data-mp="2qckjigdtcu43">
              {idType === 'alias' ? 'Enter 11-digit mobile number starting with 03' : 'Enter 24-character IBAN (e.g. PK36HABB0000001123456702)'}
            </p>
          </div>

          <div className="pt-4 flex flex-col space-y-3" data-id="element-685" data-mp="d776i8yl7hih">
            <button type="submit" disabled={!idValue} className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors" data-id="element-686" data-mp="d5mcqlk3k2lo">
              Validate Details
            </button>
            <button type="button" onClick={onCancel} className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-687" data-mp="vyq94eop8tjz">
              Cancel
            </button>
          </div>
        </form>}

      {step === 'confirm' && <div className="max-w-sm mx-auto w-full" data-id="element-688" data-mp="r16v9fvwyisk">
          {/* Verified badge */}
          <div className="flex items-center gap-2 mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5" data-id="element-689" data-mp="37m6f069s8u1e">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-690" data-mp="3bn8fsrjcle5h" />
            <span className="text-sm font-medium text-green-800" data-id="element-691" data-mp="79345ye5acic">
              Account Verified Successfully
            </span>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6 shadow-sm" data-id="element-692" data-mp="3uc7xsjkb3g1m">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-693" data-mp="3pdegeg1wf4w6">
              <h3 className="font-semibold text-gray-800" data-id="element-694" data-mp="3pf7mnu8lnn3n">Account Details</h3>
            </div>
            <div className="p-4 space-y-4" data-id="element-695" data-mp="q03z8bc59nwm">
              <div data-id="element-696" data-mp="lw60w2un8h4m">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-697" data-mp="lxpipxvy708i">
                  Account Title
                </p>
                <div className="flex items-center gap-2" data-id="element-698" data-mp="h3cwjg337at2">
                  <User className="w-4 h-4 text-gray-400" data-id="element-699" data-mp="c5tjgji4l42p" />
                  <p className="font-semibold text-gray-900" data-id="element-700" data-mp="19lqlsracxogj">
                    {mockAccountTitle}
                  </p>
                </div>
              </div>
              <div data-id="element-701" data-mp="14tz65bsqp356">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-702" data-mp="14tfkpl3vrwme">
                  Bank Name
                </p>
                <div className="flex items-center gap-2" data-id="element-703" data-mp="1h4q3msi677xs">
                  <Landmark className="w-4 h-4 text-gray-400" data-id="element-704" data-mp="1h7cmvpefbbvu" />
                  <p className="font-medium text-gray-900" data-id="element-705" data-mp="1m3tbekpjbs9g">{mockBankName}</p>
                </div>
              </div>
              <div data-id="element-706" data-mp="1q7iw2z339pp7">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-707" data-mp="1q3v3c25gx7bj">
                  {idType === 'alias' ? 'Mobile Number' : 'IBAN'}
                </p>
                <div className="flex items-center gap-2" data-id="element-708" data-mp="202sznr94y553">
                  <Building className="w-4 h-4 text-gray-400" data-id="element-709" data-mp="24ukhnde872jm" />
                  <p className="font-medium text-gray-900 font-mono" data-id="element-710" data-mp="24xgd8v2lpf4w">
                    {idValue}
                  </p>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-100" data-id="element-711" data-mp="2dx3f1hz8smyh">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-712" data-mp="2iulp5bozx882">
                  Amount to Request
                </p>
                <p className="text-lg font-bold text-blue-600" data-id="element-713" data-mp="12wdksyb4y50a">
                  {formatPrice(totalAmount)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 text-blue-800 text-sm p-4 rounded-lg mb-6" data-id="element-714" data-mp="1cs73z0jd0wr1">
            A payment request will be sent to this account. You will need to
            approve it from your banking app.
          </div>

          <div className="flex flex-col space-y-3" data-id="element-715" data-mp="1ll0l73ida67j">
            <button onClick={handleSendRequest} className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-716" data-mp="1qkd8fpd5hfan">
              Send Payment Request
            </button>
            <button onClick={() => setStep('input')} className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-717" data-mp="20ggh7e2icc6k">
              Back
            </button>
          </div>
        </div>}

      {step === 'waiting' && <div className="text-center max-w-sm mx-auto w-full" data-id="element-718" data-mp="2eol5escg0swo">
          {isExpired ? <div className="bg-red-50 border border-red-200 rounded-xl p-8 flex flex-col items-center" data-id="element-719" data-mp="2emi7zn8ynxhx">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" data-id="element-720" data-mp="2akdj11tgjp1m" />
              <h3 className="text-lg font-bold text-red-900 mb-2" data-id="element-721" data-mp="25p7stqt0vo7q">
                Request Expired
              </h3>
              <p className="text-sm text-red-700 mb-6 text-center" data-id="element-722" data-mp="32bt9j6y4xvx4">
                The payment request was not approved in time. Please try again.
              </p>
              <button onClick={() => {
          setStep('input');
          setTimeLeft(300);
          setIsExpired(false);
        }} className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-723" data-mp="2tbxu2pxjrera">
                Try Again
              </button>
            </div> : <div className="flex flex-col items-center py-4" data-id="element-724" data-mp="1eg4yo3g7yany">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6 relative" data-id="element-725" data-mp="1ego9z1bd60t0">
                <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-ping opacity-75" data-id="element-726" data-mp="1jd55igkw7hg4"></div>
                <CheckCircle className="w-10 h-10 text-blue-600 relative z-10" data-id="element-727" data-mp="1aejgc2kx146d" />
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-728" data-mp="vpnbsfi2i9ai">
                Request Sent Successfully
              </h3>
              <p className="text-sm text-gray-600 mb-6 px-4" data-id="element-729" data-mp="10gn4t9o2g77q">
                Please open your banking app and approve the payment request of{' '}
                <span className="font-bold" data-id="element-730" data-mp="mmvwg4m2mtdl">{formatPrice(totalAmount)}</span>.
              </p>

              <div className="flex items-center justify-center space-x-2 text-sm font-medium text-gray-700 bg-gray-100 px-6 py-3 rounded-full mb-6" data-id="element-731" data-mp="qohfy8t425wr">
                <Clock className="h-5 w-5 text-blue-600" data-id="element-732" data-mp="hq5grie6hzsp" />
                <span data-id="element-733" data-mp="hrqbp94enkr3">
                  Expires in:{' '}
                  <span className="text-blue-600 font-bold text-base" data-id="element-734" data-mp="3ua9iulimtnrd">
                    {formatTime(timeLeft)}
                  </span>
                </span>
              </div>

              {/* User Guidelines */}
              <div className="w-full bg-gray-50 border border-gray-200 rounded-xl mb-6 overflow-hidden text-left" data-id="element-735" data-mp="83l1nhnyh1ht">
                <button onClick={() => setShowGuide(!showGuide)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-736" data-mp="359oawygc8uu">
                  <span className="flex items-center gap-2" data-id="element-737" data-mp="c20vo5igfa4q">
                    <SmartphoneIcon className="w-4 h-4 text-blue-600" data-id="element-738" data-mp="qrg9kkwvzl30" />
                    How to approve from your banking app?
                  </span>
                  {showGuide ? <ChevronUpIcon className="w-4 h-4 text-gray-500" data-id="element-739" data-mp="zsxwon8pon17" /> : <ChevronDownIcon className="w-4 h-4 text-gray-500" data-id="element-740" data-mp="ztpgz1wuyyc6" />}
                </button>
                {showGuide && <div className="px-4 pb-4 pt-1 border-t border-gray-200" data-id="element-741" data-mp="vprkkyyx41mt">
                    <div className="space-y-4" data-id="element-742" data-mp="2pa1tq9deo63l">
                      <div className="flex items-start gap-3" data-id="element-743" data-mp="2u9eiiznu7n8t">
                        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-744" data-mp="2u7l7z9a3sivj">
                          <span className="text-xs font-bold text-blue-700" data-id="element-745" data-mp="33z7s5v4f5saf">
                            1
                          </span>
                        </div>
                        <div data-id="element-746" data-mp="26dtmj00u1l5n">
                          <p className="text-sm font-medium text-gray-800" data-id="element-747" data-mp="2bak0nht2u6of">
                            Open your banking app
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-748" data-mp="2g8ljc1mlc3zd">
                            Launch your bank's mobile app (e.g. HBL Mobile,
                            JazzCash, Easypaisa)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-749" data-mp="3vpc7vgh8hr5b">
                        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-750" data-mp="1k31x58zgvrx8">
                          <span className="text-xs font-bold text-blue-700" data-id="element-751" data-mp="1g2rmhj0qcdc1">
                            2
                          </span>
                        </div>
                        <div data-id="element-752" data-mp="16c5cc4bs8f0q">
                          <p className="text-sm font-medium text-gray-800 flex items-center gap-1" data-id="element-753" data-mp="16b3xdornb8dq">
                            Check notifications
                            <BellRingIcon className="w-3.5 h-3.5 text-amber-500" data-id="element-754" data-mp="wdphprdjqnmh" />
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-755" data-mp="sbug0lrpe886">
                            You will receive a RAAST payment request
                            notification. Tap on it to view details.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-756" data-mp="2hrbrr2wxbdeb">
                        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-757" data-mp="2hrtv0ji06rvq">
                          <span className="text-xs font-bold text-blue-700" data-id="element-758" data-mp="35mdsfqjf3ein">
                            3
                          </span>
                        </div>
                        <div data-id="element-759" data-mp="2rwttywwvd3f5">
                          <p className="text-sm font-medium text-gray-800" data-id="element-760" data-mp="2wu3jeck9df2z">
                            Review payment details
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-761" data-mp="3kk64u9to3msm">
                            Verify the merchant name (Gadgets Zone) and amount (
                            {formatPrice(totalAmount)}) are correct.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-762" data-mp="3fiqlcm751occ">
                        <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-763" data-mp="1z61yy2rjikte">
                          <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-764" data-mp="246prwtzbxbq9" />
                        </div>
                        <div data-id="element-765" data-mp="1wo00ghgh41f8">
                          <p className="text-sm font-medium text-gray-800" data-id="element-766" data-mp="1rn2j273afbes">
                            Approve the payment
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-767" data-mp="25m1zirgncjzv">
                            Tap "Approve" or "Accept" and authenticate with your
                            PIN/biometrics. This page will update automatically.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>

              <p className="text-xs text-gray-500 animate-pulse" data-id="element-768" data-mp="1mohho4caulor">
                Waiting for your approval...
              </p>

              <button onClick={onCancel} className="mt-6 text-sm text-gray-500 hover:text-gray-700 underline" data-id="element-769" data-mp="1huwi47v25jng">
                Cancel Payment
              </button>
            </div>}
        </div>}
    </div>;
}